/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Heading, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { reaction } from 'mobx';
import { getDeliveryOptionsAsync } from '../../actions/farmlands-get-delivery-options-calls.action';
import LinksComponent from './components/links';
import { IFarmlandsDeliveryOptionsData } from './farmlands-delivery-options.data';
import { IFarmlandsDeliveryOptionsProps } from './farmlands-delivery-options.props.autogenerated';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getCustomer, GetCustomerInput } from '@msdyn365-commerce-modules/retail-actions';
import {
    ProjectionDomain,
    AffiliationLoyaltyTier,
    CustomerAffiliation,
    ProductPrice,
    CommerceProperty
} from '@msdyn365-commerce/retail-proxy';

export interface IContentBlockViewProps extends IFarmlandsDeliveryOptionsProps<IFarmlandsDeliveryOptionsData> {
    title?: React.ReactNode;
    text?: React.ReactNode;
    image?: React.ReactNode;
    links?: React.ReactNode;
    className?: string;
    contentBlockContainer: IModuleProps;
    imageContainer: INodeProps;
    detailsContainer: INodeProps;
    deliveryData?: any;
}
export interface IDeliveryOptionsComponentState {
    products: any[];
    deliveryData: any;
}

let deliveryData: any;

/**
 *
 * ContentCard component
 * @extends {React.PureComponent<IFarmlandsDeliveryOptionsProps<IFarmlandsDeliveryOptionsData>>}
 */
class ContentBlock extends React.PureComponent<
    IFarmlandsDeliveryOptionsProps<IFarmlandsDeliveryOptionsData>,
    IDeliveryOptionsComponentState
> {
    constructor(props: IFarmlandsDeliveryOptionsProps<IFarmlandsDeliveryOptionsData>) {
        super(props);
        this.state = {
            products: [this.props.data.product],
            deliveryData: null,
        };
    }

    public async componentDidMount(): Promise<void> {
        const { data, context } = this.props;
        const { product } = this.props.data;
        // set deliveryData on load of the module
        const recordId = await product?.result?.RecordId;
        await this._getActivePrice();
        const salesPriceInclGST = this.state.products[0].result.ExtensionProperties?.find(
            (prop: CommerceProperty) => prop.Key === 'ItemPriceWithGST'
        )?.Value?.DecimalValue;
        const response = await getDeliveryOptionsAsync({
            context: { callerContext: this.props.context.actionContext, queryResultSettings: {} },
            recordId,
            salesPriceInclGST
        });
        deliveryData = await Promise.all(response.DeliveryOptions);
        this.setState({ deliveryData: deliveryData });
        // run below logic when the product variant is selected
        // @ts-ignore: Compiler not reconizing condition check for function params
        reaction(
            () => (data.product.result ? data.product.result.RecordId : null),
            async () => {
                if (context && data.product.result) {
                    const variantProduct = data.product;
                    const variantRecordId = await variantProduct?.result?.RecordId;
                    const variantSalesPriceInclGST = await variantProduct.result.AdjustedPrice;
                    const variantResponse = await getDeliveryOptionsAsync({
                        context: { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                        recordId: variantRecordId,
                        salesPriceInclGST: variantSalesPriceInclGST
                    });
                    deliveryData = await Promise.all(variantResponse.DeliveryOptions);
                    this.setState({ deliveryData: deliveryData });
                }
            }
        );
    }

    public render(): JSX.Element | null {
        const { heading, paragraph, image, links, className } = this.props.config;
        const contentBlockTitle = heading && heading.text && (
            <Heading
                className='farmlands-delivery-options__content-block__title'
                headingTag={heading.tag}
                text={heading.text}
            />
        );
        const imageProps = {
            gridSettings: this.props.context.request.gridSettings || {},
            imageSettings: image && image.imageSettings
        };
        const contentBlockLinks = links && links.length > 0 && <LinksComponent {...{ links }} />;
        const contentBlockText = paragraph && (
            <Msdyn365.RichTextComponent text={paragraph} className='farmlands-delivery-options__content-block__text' />
        );
        const contentBlockImage = image && <Msdyn365.Image {...image} {...imageProps} />;

        if (!contentBlockTitle && !contentBlockText && !contentBlockImage && !contentBlockLinks) {
            this.props.context.telemetry.error('Content block content is empty, module wont render.');
            return null;
        }
        const contentBlockviewProps = {
            ...this.props,
            title: contentBlockTitle,
            text: contentBlockText,
            image: contentBlockImage,
            links: contentBlockLinks,
            moduleClass: this.props.config.className,
            contentBlockContainer: {
                moduleProps: this.props,
                className: classnames('farmlands-delivery-options__content-block', className)
            },
            imageContainer: { className: 'farmlands-delivery-options__content-block__image' },
            detailsContainer: { className: 'farmlands-delivery-options__content-block__details' },
            deliveryData: this._createDeliveryTable(this.state.deliveryData)
        };

        return this.props.renderView(contentBlockviewProps) as React.ReactElement;
    }

    private _createDeliveryTable(deliveryData: IFarmlandsDeliveryOptionsData | undefined): React.ReactNode | undefined {
        const { pickUpTableHeader, deliveryTableHeader } = this.props.config;
        const deliveryOption = this.removePickUpFromDeliveryArray(deliveryData);
        return deliveryData ? (
            <table className='farmlands-delivery-options__table'>
                <tr>
                    <th />
                    <th>{pickUpTableHeader}</th>
                    <th>{deliveryTableHeader}</th>
                </tr>
                {deliveryOption.length > 0 ? (
                    // Add delivery options to the table
                    deliveryOption.map((option: any, index: number) => {
                        return (
                            <tr key={index}>
                                <td className='farmlands-delivery-options__table-header'>
                                    {option.DeliveryModes[0].ChargeDescription}
                                </td>
                                <td>{this.isPickUpAvailable(deliveryData) ? 'FREE' : 'UNAVAILABLE'}</td>
                                <td>${option.DeliveryModes[0].ChargeAmount?.toFixed(2)}</td>
                            </tr>
                        );
                    })
                ) : (
                    // If no delivery options are available
                    <tr>
                        <td className='farmlands-delivery-options__table-header'>Standard Item</td>
                        <td>FREE</td>
                        <td>UNAVAILABLE</td>
                    </tr>
                )}
            </table>
        ) : undefined;
    }

    private removePickUpFromDeliveryArray = (response: any) => {
        return response?.filter((deliveryMethod: any) => deliveryMethod.DeliveryType === 'Delivery');
    };

    private isPickUpAvailable = (response: any): boolean => {
        const isPickUpAvailable = response?.filter((deliveryMethod: any) => deliveryMethod.DeliveryType === 'Pickup');
        return isPickUpAvailable.length > 0;
    };

    private _getActivePrice = async (): Promise<void> => {
        let products = this.state.products || [];
        const productRecIds = products && products.length > 0 ? products.map((prod) => prod.result.RecordId) : [];
        const projectDomain: ProjectionDomain = {
            ChannelId: this.props.context.request.apiSettings.channelId,
            CatalogId: this.props.context.request.apiSettings.catalogId
        };
        const affliations: AffiliationLoyaltyTier[] = [];
        const customerInput = new GetCustomerInput(this.props.context.actionContext.requestContext.apiSettings);
        const customer = await getCustomer(customerInput, this.props.context.actionContext);

        if (customer && customer.CustomerAffiliations) {
            customer.CustomerAffiliations.forEach((affliation: CustomerAffiliation) => {
                const aff: AffiliationLoyaltyTier = {
                    AffiliationId: affliation.RetailAffiliationId,
                    CustomerId: customer.AccountNumber
                };
                affliations.push(aff);
            });
        }

        await getActivePricesAsync(
            { callerContext: this.props.context.actionContext, queryResultSettings: {} },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            affliations,
            true
        )
            .then((activePricesResponse: ProductPrice[]) => {
                products = products.map((prod) => {
                    const activePrice = activePricesResponse.find(
                        (product) => product.ProductId === prod.result.RecordId
                    );
                    if (activePrice) {
                        const priceIncGST = activePrice.ExtensionProperties?.find(
                            (prop: CommerceProperty) => prop.Key === 'BasePriceWithGST'
                        );
                        const specialPriceIncGST = activePrice.ExtensionProperties?.find(
                            (prop: CommerceProperty) => prop.Key === 'ItemPriceWithGST'
                        );
                        prod.result.ExtensionProperties.push(priceIncGST);
                        prod.result.ExtensionProperties.push(specialPriceIncGST);
                    }
                    return prod;
                });
                this.setState({
                    products: products
                });
            })
            .catch((error: Error) => {
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
                this.props.context.actionContext.telemetry.debug(
                    `[get Affiliation based price on Product Collection]Error executing action`
                );
                throw new Error('[get Affiliation based price on Product Collection]Error executing action');
            });
    };
}

export default ContentBlock;
